<template>
  <VDialog v-model="dialog" persistent max-width="760">
    <template #activator="{ on }">
      <slot name="activator" :on="show">
        <VBtn v-if="$route.meta.supportBtn" id="supportBtn" class="support-btn" fab dark large color="light-green" v-on="on">
          <VIcon large dark>
            {{ supportIcon }}
          </VIcon>
        </VBtn>
      </slot>
    </template>
    <VCard class="support">
      <div class="d-flex justify-end relative pr-1">
        <VBtn icon small absolute class="mt-1" @click="close">
          <VIcon>
            {{ closeIcon }}
          </VIcon>
        </VBtn>
      </div>
      <VCardTitle class="pb-0">
        <span class="headline">{{ $t('dialogTitle') }}</span>
      </VCardTitle>
      <VCardText class="py-0 px-7">
        <VContainer class="pa-0">
          <VRow>
            <VCol cols="12" class="py-1">
              <VRow>
                <VCol cols="12" class="py-1">
                  <VRow no-gutters>
                    <VCol cols="12">
                      <span class="title">
                        1. {{ $t('subjectTitle') }}
                      </span>
                    </VCol>
                  </VRow>
                  <VRow no-gutters>
                    <VCol cols="12">
                      <VTextField
                        v-model="input.subject"
                        outlined
                        dense
                        hide-details="auto"
                        :error-messages="error.errors.subject"
                      >
                        <template v-slot:label>
                          {{ $t('subjectInputLabel') }} <span class="red--text">*</span>
                        </template>
                      </VTextField>
                    </VCol>
                  </VRow>
                </VCol>
              </VRow>
              <VRow>
                <VCol cols="12">
                  <VRow no-gutters>
                    <VCol cols="12">
                      <span class="title">
                        2. {{ $t('commentTitle') }}
                      </span>
                    </VCol>
                  </VRow>
                  <VRow no-gutters>
                    <VCol cols="12">
                      <VTextarea
                        v-model="input.comment"
                        :rows="2"
                        outlined
                        dense
                        hide-details="auto"
                        :error-messages="error.errors.comment"
                      >
                        <template v-slot:label>
                          {{ $t('commentInputLabel') }} <span class="red--text">*</span>
                        </template>
                      </VTextarea>
                    </VCol>
                  </VRow>
                </VCol>
              </VRow>
              <VRow>
                <VCol cols="12" class="py-1">
                  <VRow no-gutters>
                    <VCol cols="12">
                      <span class="title">
                        3. {{ $t('contactsTitle') }}
                      </span>
                    </VCol>
                  </VRow>
                  <VRow no-gutters>
                    <VCol cols="12">
                      <VRow no-gutters class="mb-3">
                        <VCol cols="12">
                          <VTextField
                            v-model="input.requester.name"
                            outlined
                            dense
                            hide-details="auto"
                            :error-messages="error.errors['requester.name']"
                          >
                            <template v-slot:label>
                              {{ $t('contactsNameLabel') }} <span class="red--text">*</span>
                            </template>
                          </VTextField>
                        </VCol>
                      </VRow>
                      <VRow no-gutters class="mb-3">
                        <VCol cols="12">
                          <VTextField
                            v-model="input.requester.email"
                            :label="$t('contactsEmailLabel')"
                            outlined
                            dense
                            type="email"
                            autocomplete="email"
                            hide-details="auto"
                            :error-messages="error.errors['requester.email']"
                          >
                            <template v-slot:label>
                              {{ $t('contactsEmailLabel') }} <span class="red--text">*</span>
                            </template>
                          </VTextField>
                        </VCol>
                      </VRow>
                      <VRow no-gutters class="mb-3">
                        <VCol cols="12">
                          <VTextField
                            v-model="input.requester.phone"
                            :label="$t('contactsPhoneLabel')"
                            outlined
                            dense
                            type="tel"
                            autocomplete="tel"
                            hide-details="auto"
                            :error-messages="error.errors['requester.phone']"
                          />
                        </VCol>
                      </VRow>
                    </VCol>
                  </VRow>
                </VCol>
              </VRow>
              <VRow>
                <VCol cols="12" class="text-center pt-0">
                  <VBtn color="primary" :loading="loading" dark @click="send">
                    {{ $t('send') }}
                  </VBtn>
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </VContainer>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<script>
import { detect as detectBrowser } from 'detect-browser';
import { mdiCloseCircleOutline, mdiHelp } from '@mdi/js';
import ApiMixin from '@/mixins/ApiMixin';
import RecaptchaMixin from '@/mixins/RecaptchaMixin';
import httpClient from '@/api';
import { zendeskFields } from '@/enums';
import Logrocket from '@/logrocket';
import { getDeviceInfo } from '@/functions';

export default {
  name: 'SupportTicketDialog',
  mixins: [ApiMixin, RecaptchaMixin],
  props: {
    candidate: {
      validator: prop => typeof prop === 'object' || prop === null,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      closeIcon: mdiCloseCircleOutline,
      supportIcon: mdiHelp,
      dialog: false,
      error: this.getInitialErrorData(),
      loading: false,
      input: {
        subject: null,
        comment: null,
        requester: {
          name: this.candidate ? `${this.candidate.firstName} ${this.candidate.lastName}` : null,
          email: this.candidate ? this.candidate.email : null,
          phone: this.candidate ? this.candidate.phone : null,
        },
      },
    };
  },
  watch: {
    candidate(val) {
      if (!val) {
        return;
      }

      this.$set(this.input, 'requester', {
        name: `${this.candidate.firstName} ${this.candidate.lastName}`,
        email: this.candidate.email,
        phone: this.candidate.phone,
      });
    },
  },
  methods: {
    show() {
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    send() {
      this.withApiErrorHandling(async () => {
        const browser = detectBrowser();
        const devicesInfo = await getDeviceInfo();

        await httpClient.post('tickets', {
          subject: this.input.subject,
          comment: this.input.comment,
          requester: this.input.requester,
          fields: [
            { id: zendeskFields.CURRENT_PAGE, value: window.location.href },
            { id: zendeskFields.USER_AGENT, value: window.navigator.userAgent },
            { id: zendeskFields.APP, value: 'frontend' },
            { id: zendeskFields.MEDIA_DEVICES, value: devicesInfo },
            { id: zendeskFields.OS, value: browser ? browser.os : null },
            { id: zendeskFields.BROWSER, value: browser ? `${browser.name} ${browser.version}` : null },
            { id: zendeskFields.LOGROCKET_URL, value: Logrocket.sessionURL },
          ],
          recaptchaToken: await this.getRecaptchaToken('companyRegistration'),
        });
        this.close();
      }, 'error', 'loading');
    },
  },
};
</script>

<style lang="scss" scoped>
.title {
  display: block;
  margin-bottom: 7px;
}

.v-dialog__content {
  @media screen and (max-width: 1440px) {
    height: 70% !important;
  }
}
</style>

<style lang="scss">
.support-btn {
  z-index: 10;
  right: 30px;
  bottom: 5%;
  &.v-btn {
    position: fixed;
  }
}

.support .v-card__title {
  word-break: normal;
}

.support .v-card__text {
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}
</style>

<i18n locale="en" lang="yaml">
  dialogTitle: Setup help
  subjectTitle: Subject
  subjectInputLabel: Subject
  commentTitle: Describe the issue
  commentInputLabel: Describe how the issue occurred
  contactsTitle: Your contacts
  contactsNameLabel: Name
  contactsEmailLabel: E-mail
  contactsPhoneLabel: Phone number
  send: Send
</i18n>

<i18n locale="ru" lang="yaml">
  dialogTitle: Помощь в настройке оборудования
  subjectTitle: Тема обращения
  subjectInputLabel: Тема обращения
  commentTitle: Опишите проблему
  commentInputLabel: Опишите, как возникла проблема
  contactsTitle: Как с вами связаться
  contactsNameLabel: Имя
  contactsEmailLabel: Email
  contactsPhoneLabel: Телефон
  send: Отправить
</i18n>
